/**
 * Currency symbols for salary display.
 *
 * Kept byte-identical to landing's `src/constants/currency.ts` — the two apps
 * showing the same job as "$180K" and "USD180k" is the bug this exists to fix,
 * so the maps have to stay in lockstep. There is no shared package between the
 * repos; if you edit one, edit the other.
 *
 * Mirrors the backend's `/currency/symbols` map but curated rather than copied
 * wholesale, for two reasons that map straight onto display bugs:
 *
 * 1. That map returns a bare "$" for 26 different currencies — USD, CAD, AUD,
 *    SGD, HKD, MXN and friends. Rendering a Canadian salary as "$90K" reads as
 *    US dollars, so the dollar family carries its locale-standard prefix here.
 * 2. Some entries are non-Latin glyphs ("د.إ" for AED, "﷼" for SAR). On a job
 *    card those are less legible than the ISO code, so currencies without a
 *    widely-recognised Latin glyph are deliberately absent and fall back to
 *    "AED 50K" via getCurrencySymbol.
 *
 * A code missing from this map is not a bug — it renders as `CODE ` + amount,
 * which is still unambiguous money.
 */
export const CURRENCY_SYMBOLS: Record<string, string> = {
  INR: '₹',
  USD: '$',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  CNY: '¥',

  // The dollar family — prefixed so they can't be mistaken for each other.
  AUD: 'A$',
  CAD: 'C$',
  SGD: 'S$',
  HKD: 'HK$',
  NZD: 'NZ$',
  TWD: 'NT$',
  MXN: 'MX$',
  BRL: 'R$',

  // Other widely-recognised Latin or near-Latin glyphs.
  CHF: 'CHF ',
  SEK: 'kr',
  NOK: 'kr',
  DKK: 'kr',
  PLN: 'zł',
  CZK: 'Kč',
  HUF: 'Ft',
  TRY: '₺',
  RUB: '₽',
  UAH: '₴',
  ILS: '₪',
  KRW: '₩',
  THB: '฿',
  PHP: '₱',
  VND: '₫',
  IDR: 'Rp',
  MYR: 'RM',
  ZAR: 'R',
  NGN: '₦',
  KES: 'KSh',
  BDT: '৳',
  // Pakistan, Sri Lanka and Nepal all use "₨" — the code disambiguates them,
  // same reasoning as the dollar family.
  PKR: 'PKR ',
  LKR: 'LKR ',
  NPR: 'NPR ',
};

/** Lakh/crore is Indian notation, so only these render as "1.5L" / "2Cr". */
export const INR_ALIASES = ['INR', '₹'];
